/** Date formatting utilities. */

import i18n, { toAppLanguage } from "@/i18n";
import { getLocale } from "@/i18n/formatters";
import { safeParseDate } from "../core/timestamps";

const getActiveLocale = () =>
  getLocale(toAppLanguage(i18n.resolvedLanguage || i18n.language));

const parseDisplayDate = (date: unknown) => {
  if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [year, month, day] = date.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  return safeParseDate(date);
};

export const formatDate = (date: unknown): string =>
  parseDisplayDate(date).toLocaleDateString(getActiveLocale(), {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

export const formatDateTime = (date: unknown): string =>
  safeParseDate(date).toLocaleString(getActiveLocale(), {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

export const formatDateTimeCompact = (date: unknown): string =>
  safeParseDate(date).toLocaleDateString(getActiveLocale(), {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

export const getRelativeTime = (date: unknown): string => {
  const difference = Date.now() - safeParseDate(date).getTime();
  const seconds = Math.floor(difference / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const formatter = new Intl.RelativeTimeFormat(getActiveLocale(), {
    numeric: "auto",
  });

  if (days > 0) return formatter.format(-days, "day");
  if (hours > 0) return formatter.format(-hours, "hour");
  if (minutes > 0) return formatter.format(-minutes, "minute");
  return formatter.format(0, "second");
};

export const formatAppointmentDisplayDate = (date: unknown): string =>
  safeParseDate(date).toLocaleDateString(getActiveLocale(), {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

export const formatAppointmentTime = (date: unknown): string =>
  safeParseDate(date).toLocaleTimeString(getActiveLocale(), {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
